/**
 * projectEvents — PURE helpers for the COMMS rail (project event stream).
 *
 * Contract: plans/2026-08-27-comms-rail-api-contract.md. The server is the
 * ONE formatter: `text` is a single sentence whose only markup is `**bold**`
 * spans; everything else is literal. These helpers turn that into React-safe
 * segments (never HTML strings), merge paginated pages by id, and label time.
 * No React, no fetch — unit-tested in projectEvents.test.js.
 */

/**
 * renderEventText — split a `**bold**` sentence into segments.
 * `'**A** handed **B** to C'` → [{bold:true,text:'A'},{bold:false,text:' handed '},…].
 * An unclosed `**` is literal text (never swallowed). Empty segments dropped.
 *
 * @param {string} text
 * @returns {Array<{bold: boolean, text: string}>}
 */
export function renderEventText(text) {
  const s = typeof text === 'string' ? text : '';
  const out = [];
  let i = 0;
  let bold = false;
  let buf = '';
  while (i < s.length) {
    if (s.startsWith('**', i)) {
      // Opening a bold span needs a matching close; otherwise it is literal.
      if (!bold && s.indexOf('**', i + 2) === -1) {
        buf += '**';
        i += 2;
        continue;
      }
      if (buf) out.push({ bold, text: buf });
      buf = '';
      bold = !bold;
      i += 2;
      continue;
    }
    buf += s[i];
    i += 1;
  }
  if (buf) out.push({ bold, text: buf });
  return out;
}

/**
 * mergeEventPages — flatten react-query pages into ONE list, deduped by `id`
 * (a poll of page 1 overlaps older pages), ordered id DESC (ids are
 * `events:<zero-padded ms>-<hex>` — lexical order IS time order, so newest
 * first). The first occurrence of an id wins (page order = freshest first).
 *
 * @param {Array<{events?: Array<{id: string}>}>} pages
 * @returns {Array<object>}
 */
export function mergeEventPages(pages) {
  const seen = new Set();
  const out = [];
  for (const page of Array.isArray(pages) ? pages : []) {
    for (const ev of Array.isArray(page?.events) ? page.events : []) {
      if (!ev || typeof ev.id !== 'string' || seen.has(ev.id)) continue;
      seen.add(ev.id);
      out.push(ev);
    }
  }
  // A run's start / tick_plan / finish rows are folded SERVER-SIDE into one
  // `run_summary` (contract §collapse). A poll may still hold the earlier live
  // `run_started`; once the summary is here those rows are superseded and
  // dropped — otherwise the run would be two lines.
  const summarized = new Set();
  for (const ev of out) if (ev.kind === 'run_summary' && ev.executionId) summarized.add(ev.executionId);
  const kept = summarized.size
    ? out.filter((ev) => !(RUN_ROW_KINDS[ev.kind] && ev.executionId && summarized.has(ev.executionId)))
    : out;
  kept.sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  return kept;
}

/** Row kinds a `run_summary` supersedes (same executionId). */
export const RUN_ROW_KINDS = Object.freeze({ run_started: 1, run_finished: 1, tick_plan: 1 });

// ── NEEDS YOU deck — pure helpers ─────────────────────────────────────────
// The server derives the deck (needsYou.items: one per open decision, oldest
// first); the client only rotates through it and drops a card it just acted on.

/**
 * deckItems — the well-formed question/blocked cards out of `needsYou`.
 * A legacy count-only payload (no `items`) yields none.
 * @returns {Array<object>}
 */
export function deckItems(needsYou) {
  const items = needsYou && Array.isArray(needsYou.items) ? needsYou.items : [];
  return items.filter((i) => i && i.eventId && i.ticketKey && (i.type === 'question' || i.type === 'blocked'));
}

/**
 * deckIndex — where the deck should point after a refresh: the SAME card if
 * it is still there (matched by eventId), else the same position clamped.
 */
export function deckIndex(items, prevIdx, prevEventId) {
  const n = items.length;
  if (!n) return 0;
  if (prevEventId) {
    const i = items.findIndex((it) => it.eventId === prevEventId);
    if (i !== -1) return i;
  }
  const p = typeof prevIdx === 'number' && Number.isFinite(prevIdx) ? prevIdx : 0;
  return Math.max(0, Math.min(n - 1, p));
}

/** deckStep — ‹ › rotate with wrap-around. */
export function deckStep(idx, n, dir) {
  if (!n) return 0;
  return (((idx + dir) % n) + n) % n;
}

/**
 * deckAdvance — optimistic advance: the card just acted on leaves the deck;
 * the index stays put (the next card lands in the same slot), clamped.
 * @returns {{items: Array<object>, index: number}}
 */
export function deckAdvance(items, idx, eventId) {
  const next = items.filter((i) => i.eventId !== eventId);
  return { items: next, index: Math.max(0, Math.min(next.length - 1, idx)) };
}

/** ageLabel — relative age for the card ("just now", "4m ago", "3h ago", "2d ago"). */
export function ageLabel(at, now) {
  if (at == null || at === '') return '';
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return '';
  const nowMs = now instanceof Date ? now.getTime() : (typeof now === 'number' ? now : Date.now());
  const s = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** prLabel — "PR #118" / "MR !7" from a PR/MR url, else "PR". */
export function prLabel(url) {
  const u = String(url || '');
  let m = u.match(/\/(?:pull|pulls)\/(\d+)/);
  if (m) return `PR #${m[1]}`;
  m = u.match(/\/merge_requests\/(\d+)/);
  if (m) return `MR !${m[1]}`;
  return 'PR';
}

/**
 * cardFacts — the facts line under the question: attempts used (red when the
 * cap is reached), the PR (https only), the repo — only what is KNOWN.
 * @returns {Array<{label: string, href?: string, tone?: 'bad'|null}>}
 */
export function cardFacts(item) {
  const out = [];
  if (item.attempts != null && Number.isFinite(Number(item.attempts))) {
    const a = Number(item.attempts);
    const cap = item.maxAttempts != null && Number.isFinite(Number(item.maxAttempts)) ? Number(item.maxAttempts) : null;
    const plural = a === 1 && cap == null ? '' : 's';
    out.push({ label: `${cap != null ? `${a}/${cap}` : a} attempt${plural} used`, tone: cap != null && a >= cap ? 'bad' : null });
  }
  if (item.prUrl && /^https:\/\//.test(String(item.prUrl))) out.push({ label: prLabel(item.prUrl), href: String(item.prUrl) });
  if (item.repo) out.push({ label: String(item.repo) });
  return out;
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * eventTimeLabel — the mono meta-line time. A running event reads "running ·
 * 4m" (elapsed), a finished one "just now" / "12s" / "4m" / "3h" / "2d" ago,
 * older than a week → `MM-DD HH:mm` (local). Unparseable `at` → ''.
 *
 * @param {string} at        ISO timestamp
 * @param {number} now       epoch ms (injected — pure)
 * @param {boolean} running
 * @returns {string}
 */
export function eventTimeLabel(at, now, running = false) {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return '';
  const nowMs = Number.isFinite(now) ? now : Date.now();
  const diff = Math.max(0, nowMs - t);
  const sec = Math.floor(diff / 1000);
  let rel;
  if (sec < 5) rel = 'just now';
  else if (sec < 60) rel = `${sec}s`;
  else if (sec < 3600) rel = `${Math.floor(sec / 60)}m`;
  else if (sec < 86400) rel = `${Math.floor(sec / 3600)}h`;
  else if (sec < 7 * 86400) rel = `${Math.floor(sec / 86400)}d`;
  else {
    const d = new Date(t);
    rel = `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  if (running) return sec < 5 ? 'running' : `running · ${rel}`;
  return rel === 'just now' ? rel : `${rel}${sec < 7 * 86400 ? ' ago' : ''}`;
}

/**
 * eventHref — resolve a meta link for THIS app.
 *   `/agent/<uuid>/logs/<exec>` (the contract's in-app shape) → the cloud
 *   deep link to that run: `/projects/<pid>/pipelines/<workflowType>/runs/<exec>`
 *   (the route App.js actually mounts — `pipelines/:workflowType/runs/:executionId`).
 *   `workflowType` is resolved from the agents list by uuid; unknown uuid →
 *   null (a link that cannot resolve is not rendered as a link).
 *   `https://…` → external (rendered target=_blank by the caller).
 *   Anything else → null.
 *
 * @returns {{href: string, external: boolean} | null}
 */
export function eventHref(href, projectId, agents) {
  if (typeof href !== 'string' || !href) return null;
  if (/^https:\/\//i.test(href)) return { href, external: true };
  const m = href.match(/^\/agent\/([^/]+)\/logs\/([^/?#]+)/);
  if (!m || !projectId) return null;
  const row = (Array.isArray(agents) ? agents : []).find((a) => a && a.uuid === m[1]);
  if (!row || !row.workflowType) return null;
  return {
    href: `/projects/${encodeURIComponent(projectId)}/pipelines/${encodeURIComponent(row.workflowType)}/runs/${encodeURIComponent(m[2])}`,
    external: false,
  };
}
